<template>
  <!-- Skip link for keyboard/screen-reader users -->
  <a href="#app-main-content" class="skip-link">Skip to main content</a>

  <!-- Navigation Bar -->
  <v-app-bar
    :color="appBarColor"
    theme="dark"
    :elevation="hideMeetingPageElevation ? 0 : undefined"
    :extension-height="breadcrumbExtensionHeight"
    data-testid="main-app-bar"
  >
    <!-- The breadcrumb trail teleports in here on a wide viewport, so its height
         becomes part of `--v-layout-top` and everything else anchored under the
         app bar clears it without knowing it exists (`breadcrumbSpine.ts`). The
         container is always present so the teleport always has a target; with no
         trail its height is 0 and the extension collapses. -->
    <template #extension>
      <div :id="BREADCRUMB_EXTENSION_ID" class="app-breadcrumbs-extension" />
    </template>
    <v-btn
      v-if="showMeetingBackButton"
      icon
      density="comfortable"
      variant="outlined"
      color="white"
      class="meeting-back-btn ml-3 mr-2"
      aria-label="Back to club meetings"
      @click="handleMeetingBack"
    >
      <v-icon :icon="mdiChevronLeft" />
    </v-btn>

    <v-app-bar-title :class="{ 'meeting-app-bar-title': showMeetingBackButton }">
      <router-link
        :to="isAuthenticated ? '/home' : '/'"
        style="text-decoration: none; color: white; width: fit-content"
        class="d-flex align-center"
        aria-label="ToastHost home"
      >
        <img
          src="@/assets/images/ToastHostLogo.png"
          alt="ToastHost Logo"
          height="32"
          :class="{ 'mr-2': !isSmAndDown }"
        />
        <span v-if="!isSmAndDown">ToastHost</span>
      </router-link>
    </v-app-bar-title>

    <!-- Public navigation links -->
    <v-btn
      v-if="!showDesktopMeetingMenu"
      variant="text"
      href="https://docs.toasthost.co.uk/reference/faq/"
      target="_blank"
      rel="noopener"
      class="mr-2 d-none d-sm-flex"
      @click="trackDocsClick('main_app_bar', 'https://docs.toasthost.co.uk/reference/faq/')"
    >
      FAQ
    </v-btn>
    <v-btn v-if="!showDesktopMeetingMenu" variant="text" to="/privacy" class="mr-2 d-none d-sm-flex"> Privacy </v-btn>
    <v-btn
      v-if="!showDesktopMeetingMenu"
      variant="outlined"
      href="https://docs.toasthost.co.uk"
      target="_blank"
      size="small"
      class="docs-pill mr-2 d-none d-sm-flex"
      @click="trackDocsClick('main_app_bar', 'https://docs.toasthost.co.uk')"
    >
      <v-icon :icon="mdiBookOpenPageVariant" size="16" class="mr-1" />
      Docs
    </v-btn>

    <!-- Admin link for admin users -->
    <v-btn
      v-if="!showDesktopMeetingMenu && isAuthenticated && user?.isAdmin"
      variant="text"
      class="mr-2 d-none d-sm-flex"
      to="/admin"
    >
      <v-icon class="mr-2" :icon="mdiShieldAccount" />
      Admin
    </v-btn>

    <!-- Teleport target for MeetingStatusChip on mobile (including guests) -->
    <div id="meeting-status-chip-target" class="d-flex align-center mr-2" />

    <!-- Teleport target for meeting Share button (desktop meeting pages only) -->
    <div v-if="showDesktopMeetingMenu" id="meeting-share-button-desktop-target" class="d-flex align-center mr-1" />

    <!-- Desktop meeting overflow menu (FAQ/Privacy/Admin) -->
    <v-menu v-if="showDesktopMeetingMenu">
      <template #activator="{ props }">
        <v-btn icon v-bind="props" aria-label="Meeting menu">
          <v-icon :icon="mdiDotsVertical" />
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          href="https://docs.toasthost.co.uk/reference/faq/"
          target="_blank"
          rel="noopener"
          title="FAQ"
          @click="trackDocsClick('meeting_menu', 'https://docs.toasthost.co.uk/reference/faq/')"
        />
        <v-list-item to="/privacy" title="Privacy" />
        <v-list-item
          href="https://docs.toasthost.co.uk"
          target="_blank"
          title="Docs"
          @click="trackDocsClick('meeting_menu', 'https://docs.toasthost.co.uk')"
        >
          <template #prepend>
            <v-icon :icon="mdiBookOpenPageVariant" />
          </template>
        </v-list-item>

        <template v-if="isAuthenticated && user?.isAdmin">
          <v-divider />
          <v-list-item to="/admin" title="Admin">
            <template #prepend>
              <v-icon :icon="mdiShieldAccount" />
            </template>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>

    <!-- Mobile menu for public links -->
    <v-menu v-if="showMobileMenu" :eager="true">
      <template #activator="{ props }">
        <v-btn icon v-bind="props">
          <v-icon :icon="mdiDotsVertical" />
        </v-btn>
      </template>
      <v-list>
        <!-- Teleport target for meeting Share button (mobile only) -->
        <div id="meeting-share-button-target" />

        <v-list-item
          href="https://docs.toasthost.co.uk/reference/faq/"
          target="_blank"
          rel="noopener"
          title="FAQ"
          @click="trackDocsClick('meeting_menu', 'https://docs.toasthost.co.uk/reference/faq/')"
        />
        <v-list-item to="/privacy" title="Privacy" />
        <v-list-item
          href="https://docs.toasthost.co.uk"
          target="_blank"
          title="Docs"
          @click="trackDocsClick('meeting_menu', 'https://docs.toasthost.co.uk')"
        >
          <template #prepend>
            <v-icon :icon="mdiBookOpenPageVariant" />
          </template>
        </v-list-item>

        <!-- Admin link in mobile menu -->
        <template v-if="isAuthenticated && user?.isAdmin">
          <v-divider />
          <v-list-item to="/admin" title="Admin">
            <template #prepend>
              <v-icon :icon="mdiShieldAccount" />
            </template>
          </v-list-item>

          <!-- Demo Mode in mobile menu (admin only) -->
          <v-divider />
          <DemoModeToggle />
        </template>
      </v-list>
    </v-menu>

    <!-- Show user info and logout when authenticated -->
    <template v-if="isAuthenticated">
      <v-menu>
        <template #activator="{ props }">
          <v-btn variant="text" class="ml-n1 mr-2" v-bind="props" :icon="isSmAndDown" aria-label="User menu">
            <v-icon :class="[{ 'mr-2': !isSmAndDown }]" :icon="mdiAccountCircle" />
            <span v-if="!isSmAndDown">{{ maskFullName(user?.fullName) }}</span>
          </v-btn>
        </template>
        <v-list>
          <template v-if="isSmAndDown && user">
            <v-list-item class="user-menu-header" lines="two">
              <v-list-item-title class="text-body-medium font-weight-medium">
                {{ maskFullName(user.fullName) || maskEmail(user.email) }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-body-small text-medium-emphasis">
                {{ maskEmail(user.email) }}
              </v-list-item-subtitle>
            </v-list-item>
            <v-divider />
          </template>

          <v-list-item to="/home" title="Home">
            <template #prepend>
              <v-icon :icon="mdiHome" />
            </template>
          </v-list-item>
          <v-list-item to="/profile" title="Your Info">
            <template #prepend>
              <v-icon :icon="mdiAccountCircleOutline" />
            </template>
          </v-list-item>
          <v-list-item to="/profile#password" title="Change Password">
            <template #prepend>
              <v-icon :icon="mdiKey" />
            </template>
          </v-list-item>

          <!-- View as Member Toggle (officers + admins only; visibility derives from rawUser) -->
          <template v-if="canViewAsMember">
            <v-divider />
            <ViewAsMemberToggle @request-enable="showEnableConfirm = true" />
          </template>

          <!-- Demo Mode Toggle (admin only) -->
          <template v-if="user?.isAdmin">
            <v-divider />
            <DemoModeToggle />
          </template>

          <v-divider />
          <v-list-subheader v-if="user?.clubs && user.clubs.length > 0"> Your Clubs </v-list-subheader>
          <v-list-item
            v-for="club in user?.clubs || []"
            :key="club.id"
            :to="`/club/${club.shortName}`"
            :title="club.name"
          >
            <template #prepend>
              <v-icon :icon="mdiDomain" />
            </template>
          </v-list-item>
          <v-divider v-if="user?.clubs && user.clubs.length > 0" />
          <v-list-item title="Logout" @click="handleLogout">
            <template #prepend>
              <v-icon :icon="mdiLogout" />
            </template>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

    <!-- Show login/signup button when not authenticated -->
    <template v-else>
      <v-btn variant="text" :to="loginRoute" class="mr-2" aria-label="Login or Signup"> LOGIN/SIGNUP </v-btn>
    </template>
  </v-app-bar>

  <v-main id="app-main-content" tabindex="-1">
    <ViewAsMemberBanner />
    <AppBreadcrumbs />
    <router-view />
  </v-main>

  <!-- Enable confirmation lives in the app shell (not inside the user menu) so
       it survives the menu closing when the toggle row is clicked. -->
  <ConfirmDialog
    v-model="showEnableConfirm"
    title="Switch to member view?"
    confirm-label="Enter member view"
    confirm-color="primary"
    :max-width="460"
    testid="view-as-member-confirm"
    @confirm="confirmEnableMemberView"
  >
    You'll see the site as a regular member would — your officer and admin tools will be hidden until you exit. This
    only changes what you see; your account privileges are unchanged. You can exit at any time from the banner or the
    account menu.
  </ConfirmDialog>

  <v-snackbar
    v-model="memberViewRedirectNotice"
    data-testid="view-as-member-redirect-snackbar"
    :timeout="8000"
    color="info"
    location="bottom"
  >
    You're in member view — officer tools are hidden on this page.
    <template #actions>
      <v-btn data-testid="view-as-member-redirect-exit" variant="text" @click="exitMemberViewFromNotice">
        Exit member view
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script lang="ts" setup>
import { getLogger } from "@/utils/logger";

const logger = getLogger("MainLayout");
import { onMounted, ref, watch, computed } from "vue";
import { useIsLocalEnv } from "@/composables/useIsLocalEnv";
import { useRouter, useRoute } from "vue-router";
import { usageAnalytics } from "@/analytics/usageAnalytics";
import {
  mdiAccountCircle,
  mdiAccountCircleOutline,
  mdiChevronLeft,
  mdiDomain,
  mdiLogout,
  mdiDotsVertical,
  mdiKey,
  mdiShieldAccount,
  mdiHome,
  mdiBookOpenPageVariant,
} from "@mdi/js";
import { useAuthStore } from "@/store/auth";
import { useDisplay } from "vuetify";
import { useDocumentTitle } from "@/composables/useDocumentTitle";
import DemoModeToggle from "@/components/DemoModeToggle.vue";
import ViewAsMemberToggle from "@/components/ViewAsMemberToggle.vue";
import ViewAsMemberBanner from "@/components/ViewAsMemberBanner.vue";
import AppBreadcrumbs from "@/components/workspace/AppBreadcrumbs.vue";
import { BREADCRUMB_EXTENSION_ID, useBreadcrumbSpineHeight } from "@/navigation/breadcrumbSpine";
import { WORKSPACE_DRAWER_BREAKPOINT } from "@/composables/useWorkspaceDisplay";
import ConfirmDialog from "@/components/common/ConfirmDialog.vue";
import { usePrivacyMode } from "@/composables/usePrivacyMode";
import { useViewAsMember } from "@/composables/useViewAsMember";

const router = useRouter();
const route = useRoute();

// Get auth store and create computed properties for reactivity
const authStore = useAuthStore();
const isAuthenticated = computed(() => authStore.isAuthenticated);
const user = computed(() => authStore.user);
const canViewAsMember = computed(() => authStore.canViewAsMember);

// Privacy mode (for masking user's own name in header)
const { maskFullName, maskEmail } = usePrivacyMode();

// View-as-Member: while member view is on, an officer/admin-gated route hides
// all officer tools. The bounce + actionable notice come from two layers:
//   1. Navigation to / bookmarking a gated route is handled by the router auth
//      guard (it redirects the masked role and calls notifyMemberViewRedirect).
//   2. Enabling member view *while already on* a gated page triggers no
//      navigation, so the guard never runs — this watcher covers that case.
// The notice itself lives in the composable (memberViewRedirectNotice) so both
// layers feed the one snackbar rendered below.
const { isViewAsMemberEnabled, memberViewRedirectNotice, setViewAsMember, notifyMemberViewRedirect } =
  useViewAsMember();
const showEnableConfirm = ref(false);

watch(isViewAsMemberEnabled, (enabled) => {
  if (!enabled) return;
  if (route.meta?.requiresOfficer || route.meta?.requiresAdmin) {
    router.push("/home");
    notifyMemberViewRedirect();
  }
});

function confirmEnableMemberView() {
  setViewAsMember(true);
  showEnableConfirm.value = false;
}

function exitMemberViewFromNotice() {
  setViewAsMember(false);
}

// Document title management
const { setDocumentTitle } = useDocumentTitle();

// Computed property for login route with redirect
const loginRoute = computed(() => {
  // Only include redirect if we're not already on a public page
  const currentPath = route.fullPath;
  const publicPaths = ["/", "/login", "/signup", "/privacy"];

  // Don't include redirect for public pages or the home page
  if (publicPaths.includes(currentPath) || currentPath === "/") {
    return "/login";
  }

  return {
    path: "/login",
    query: { redirect: currentPath },
  };
});

const display = useDisplay();

// Room for the pinned breadcrumb trail. Zero below the breakpoint (where the
// trail stays in the page) and on a route that renders none, which collapses the
// extension away. Vuetify adds this to the app bar's layout height, so `v-main`
// and every element anchored to `--v-layout-top` shift by it automatically.
const { height: breadcrumbSpineHeight } = useBreadcrumbSpineHeight();
const breadcrumbExtensionHeight = computed(() =>
  (display.width?.value ?? 0) >= WORKSPACE_DRAWER_BREAKPOINT ? breadcrumbSpineHeight.value : 0,
);

const isSmAndDown = computed(() => display.smAndDown?.value ?? display.mobile?.value ?? false);
const showMobileMenu = computed(() => display.smAndDown?.value ?? display.mobile?.value ?? false);
const isMeetingPage = computed(() => route.name === "ClubMeetingView");
const showMeetingBackButton = computed(() => isMeetingPage.value && isAuthenticated.value);
const showDesktopMeetingMenu = computed(() => !isSmAndDown.value && isMeetingPage.value);
const hideMeetingPageElevation = computed(() => isSmAndDown.value && isMeetingPage.value);

const handleMeetingBack = () => {
  const clubShortName = route.params.clubShortName as string | undefined;
  if (clubShortName) {
    router.push({ name: "ClubMeetings", params: { clubShortName } });
    return;
  }
  router.back();
};

// Highlight local/dev environment with a distinct app bar color to avoid
// confusing it with prod. `useIsLocalEnv` owns the VITE_SIMULATE_PROD override
// so docs-screenshot captures show the production blue here too.
const { isLocalEnv } = useIsLocalEnv();

const appBarColor = computed(() => (isLocalEnv.value ? "warning" : "primary"));

const authenticatedUserId = computed(() => (authStore.isAuthenticated ? (authStore.user?.id ?? null) : null));

watch(authenticatedUserId, (newUserId, oldUserId) => {
  if (newUserId !== oldUserId) {
    // Per issue #1447 the X-Session-Id deliberately spans login/logout —
    // a single browser's journey shouldn't fragment when the user signs
    // in. Reset only the queued analytics state, not the underlying sid.
    usageAnalytics.resetForAuthTransition();
  }
});

// Handle logout with navigation
const handleLogout = async () => {
  const success = await authStore.logout();
  if (success) {
    // Redirect to the homepage
    router.push("/");
  }
};

function trackDocsClick(source: "main_app_bar" | "meeting_menu", targetUrl: string) {
  usageAnalytics.trackDocsLinkClicked(source, targetUrl);
}

// Set base document title on component mount
onMounted(() => {
  // Set base title for the application
  setDocumentTitle("Toasthost");

  // Debug authentication state
  logger.info("MainLayout mounted, auth state:", {
    isAuthenticated: authStore.isAuthenticated,
    hasUser: !!authStore.user,
    initialCheckDone: authStore.initialAuthCheckDone,
  });
});

// Watch for route changes to refresh authentication state, but only when necessary
watch(
  () => router.currentRoute.value,
  (newRoute, oldRoute) => {
    // Only check authentication if:
    // 1. Not on signup or login pages
    // 2. Coming from the login/signup page (need to fetch fresh state after login)
    // 3. OR when we've been logged out but need to verify (force refresh)
    const path = newRoute.path;
    const fromLoginOrSignup = oldRoute?.path?.includes("/login") || oldRoute?.path?.includes("/signup");

    // Reset base title for main navigation routes
    const mainRoutes = ["/", "/home"];
    if (mainRoutes.includes(path)) {
      setDocumentTitle("Toasthost");
    }

    if (!path.includes("/signup") && !path.includes("/login")) {
      // Only force a check after login/signup or if we need to verify logged out state
      const needsFreshCheck = fromLoginOrSignup || (authStore.initialAuthCheckDone && !authStore.isAuthenticated);

      if (needsFreshCheck) {
        logger.info("Forcing auth check after navigation from:", oldRoute?.path);
        authStore.checkAuth(true); // Force a refresh
      }
    }
  },
  { deep: true },
);
</script>

<style scoped>
.skip-link {
  position: absolute;
  top: -100%;
  left: 16px;
  z-index: 9999;
  padding: 8px 16px;
  background: rgb(var(--v-theme-primary));
  color: white;
  text-decoration: none;
  border-radius: 0 0 4px 4px;
  font-weight: 500;
}

.skip-link:focus {
  top: 0;
}

#app-main-content:focus {
  outline: none;
}

/* The extension is the app bar's own surface, so it must not paint over the
   teleported trail's background; the trail carries the page colour itself. */
.app-breadcrumbs-extension {
  width: 100%;
  height: 100%;
}

/* Vuetify animates both the app bar's height and `v-main`'s padding over 200ms,
   and the extension makes both of them dynamic — so without this, the bar
   visibly grows into the trail on every load, and for 200ms afterwards the page
   is the wrong height: anything measuring geometry right after a route or
   viewport change reads a mid-animation value, and a page scrolled to its bottom
   drifts as the document shrinks under it (it made club-workspace-layout T2
   flaky). Elevation still animates. This app has one app bar, no navigation
   drawer and no scroll-behaviour, so the breadcrumb trail is the only thing that
   ever changes either of these. */
.v-app-bar {
  transition-property: box-shadow;
}

.v-main {
  transition: none;
}

.docs-pill.v-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.2px;
  border-color: rgba(255, 255, 255, 0.4);
  color: white;
  border-radius: 16px;
}

.docs-pill.v-btn:hover {
  border-color: rgba(255, 255, 255, 0.7);
  background-color: rgba(255, 255, 255, 0.1);
}

.meeting-back-btn.v-btn {
  border-radius: 10px !important;
}

.meeting-app-bar-title {
  margin-inline-start: 0;
}

.meeting-app-bar-title :deep(.v-app-bar-title__content) {
  padding-inline-start: 0;
}
</style>
